import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Hash } from "lucide-react";

const PLATFORMS = [
  { id: "linkedin", name: "LinkedIn" },
  { id: "x", name: "X (Twitter)" },
  { id: "instagram", name: "Instagram" },
  { id: "facebook", name: "Facebook" },
  { id: "threads", name: "Threads" },
  { id: "bluesky", name: "Bluesky" },
];

function pad(n) { return String(n).padStart(2, "0"); }

function defaultDateStr(offsetHours = 24) {
  const d = new Date(Date.now() + offsetHours * 3600 * 1000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function defaultTimeStr() {
  return "09:00";
}

export default function ScheduleDialog({
  open,
  onOpenChange,
  initial = {},          // { platform, content, hashtags, title, project_id, id }
  projects = [],         // [{id, name, domain}]
  defaultProjectId = null,
  onSaved,
}) {
  const isEdit = !!initial.id;
  const [platform, setPlatform] = useState(initial.platform || "linkedin");
  const [content, setContent] = useState(initial.content || "");
  const [hashtags, setHashtags] = useState((initial.hashtags || []).join(" "));
  const [title, setTitle] = useState(initial.title || "");
  const [projectId, setProjectId] = useState(initial.project_id || defaultProjectId || "_none");
  const [date, setDate] = useState(defaultDateStr());
  const [time, setTime] = useState(defaultTimeStr());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlatform(initial.platform || "linkedin");
    setContent(initial.content || "");
    setHashtags((initial.hashtags || []).join(" "));
    setTitle(initial.title || "");
    setProjectId(initial.project_id || defaultProjectId || "_none");
    if (initial.scheduled_at) {
      const d = new Date(initial.scheduled_at);
      setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
      setTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
    } else {
      setDate(defaultDateStr());
      setTime(defaultTimeStr());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    if (!content.trim()) { toast.error("Content is required"); return; }
    if (!date || !time) { toast.error("Pick a date and time"); return; }
    const local = new Date(`${date}T${time}:00`);
    if (isNaN(local.getTime())) { toast.error("Invalid date/time"); return; }
    if (!isEdit && local.getTime() < Date.now() - 60_000) {
      toast.error("Scheduled time is in the past");
      return;
    }
    setBusy(true);
    const payload = {
      platform,
      content: content.trim(),
      hashtags: hashtags.split(/[\s,]+/).map((h) => h.replace(/^#/, "").trim()).filter(Boolean),
      title: title.trim(),
      scheduled_at: local.toISOString(),
      project_id: projectId === "_none" ? null : projectId,
    };
    try {
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/social/queue/${initial.id}`, payload);
        saved = data;
        toast.success("Updated");
      } else {
        const { data } = await api.post("/social/queue", payload);
        saved = data;
        toast.success("Scheduled");
      }
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg border border-foreground/20" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-semibold tracking-tight text-2xl flex items-center gap-2">
            <CalendarClock className="w-5 h-5" /> {isEdit ? "Edit scheduled post" : "Schedule post"}
          </DialogTitle>
          <DialogDescription className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">
            // queue · {platform}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-mono-data text-[10px] uppercase tracking-widest">Platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="rounded-none h-10 border-foreground/30" data-testid="schedule-platform"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {PLATFORMS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-data text-[10px] uppercase tracking-widest">Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="rounded-none h-10 border-foreground/30" data-testid="schedule-project"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="_none">— None / global</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="font-mono-data text-[10px] uppercase tracking-widest">Title (optional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Internal note, e.g. 'Q3 launch teaser'"
              className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
              data-testid="schedule-title" />
          </div>

          <div className="space-y-1.5">
            <Label className="font-mono-data text-[10px] uppercase tracking-widest">Content</Label>
            <Textarea value={content} onChange={(e) => setContent(e.target.value)}
              placeholder="Your post body…"
              className="rounded-none border-foreground/30 focus-visible:ring-foreground font-sans text-sm min-h-[140px]"
              data-testid="schedule-content" />
            <p className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">{content.length} chars</p>
          </div>

          <div className="space-y-1.5">
            <Label className="font-mono-data text-[10px] uppercase tracking-widest flex items-center gap-1"><Hash className="w-3 h-3" /> Hashtags (space or comma)</Label>
            <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)}
              placeholder="saas founders growth"
              className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground font-mono-data text-sm"
              data-testid="schedule-hashtags" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-mono-data text-[10px] uppercase tracking-widest">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground font-mono-data text-sm"
                data-testid="schedule-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-data text-[10px] uppercase tracking-widest">Time (your tz)</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground font-mono-data text-sm"
                data-testid="schedule-time" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none h-10" data-testid="schedule-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none h-10 bg-foreground hover:bg-foreground/90" data-testid="schedule-save">
            <CalendarClock className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : (isEdit ? "Update" : "Add to queue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
